import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { usePlanningContext } from '../state/PlanningContext';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  isPlanningFeedAddedToday,
  isPlanningFeedAddedYesterday,
} from '../../../utils/addedToday';
import {
  countBucket,
  trackPlanningUpdatesEvent,
} from '../../../lib/planningUpdatesAnalytics';

const ScrapedDateFilter = ({ analyticsPlacement = 'scraped_date_filter' } = {}) => {
  const {
    filterByAddedToday,
    toggleFilterByAddedToday,
    filterByAddedYesterday,
    toggleFilterByAddedYesterday,
    filterByScrapedDate: filterByDateAdded,
    setFilterByScrapedDate: setFilterByDateAdded,
    applications,
    feedSummary,
    usesRemoteSummary,
  } = usePlanningContext();
  
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const [portalMounted, setPortalMounted] = useState(false);
  useEffect(() => { setPortalMounted(true); }, []);
  const [selectedDate, setSelectedDate] = useState('');
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const today = new Date();
    return today.getMonth();
  });
  const [visibleYear, setVisibleYear] = useState(() => {
    const today = new Date();
    return today.getFullYear();
  });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  // Handle outside clicks to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Sync state with context
  useEffect(() => {
    if (filterByDateAdded) {
      setSelectedDate(filterByDateAdded);
    } else {
      setSelectedDate('');
    }
  }, [filterByDateAdded]);

  useEffect(() => {
    if (!showDatePicker) return;

    const base = selectedDate ? new Date(selectedDate) : new Date();
    if (Number.isNaN(base.getTime())) return;

    setVisibleMonth(base.getMonth());
    setVisibleYear(base.getFullYear());
  }, [showDatePicker, selectedDate]);
  
  // Get today's date
  const getTodayDateString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  const handleAddedTodayClick = () => {
    trackPlanningUpdatesEvent('planning_updates_quick_date_filter_clicked', {
      placement: analyticsPlacement,
      status: 'today',
      reason: filterByAddedToday ? 'disabled' : 'enabled',
      value: addedTodayCount,
      metadata: {
        date_filter: 'today',
        enabled: !filterByAddedToday,
        result_count: addedTodayCount,
        result_count_bucket: countBucket(addedTodayCount)
      }
    });
    toggleFilterByAddedToday(getTodayDateString());
    setShowDatePicker(false);
  };

  const getYesterdayDateString = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
  };

  const handleAddedYesterdayClick = () => {
    trackPlanningUpdatesEvent('planning_updates_quick_date_filter_clicked', {
      placement: analyticsPlacement,
      status: 'yesterday',
      reason: filterByAddedYesterday ? 'disabled' : 'enabled',
      value: addedYesterdayCount,
      metadata: {
        date_filter: 'yesterday',
        enabled: !filterByAddedYesterday,
        result_count: addedYesterdayCount,
        result_count_bucket: countBucket(addedYesterdayCount)
      }
    });
    toggleFilterByAddedYesterday(getYesterdayDateString());
    setShowDatePicker(false);
  };
  
  const handleClickDateButton = () => {
    trackPlanningUpdatesEvent('planning_updates_date_picker_toggled', {
      placement: analyticsPlacement,
      status: showDatePicker ? 'closed' : 'opened',
      metadata: {
        has_custom_date: Boolean(filterByDateAdded)
      }
    });
    if (!showDatePicker && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const calendarWidth = Math.min(320, window.innerWidth - 16);
      let left = rect.left;
      if (left + calendarWidth > window.innerWidth - 8) {
        left = window.innerWidth - calendarWidth - 8;
      }
      left = Math.max(8, left);

      const minTop = 60; // clear browser chrome / safe-area
      const spaceAbove = rect.top - minTop;
      const spaceBelow = window.innerHeight - rect.bottom - 8;

      if (spaceAbove >= spaceBelow) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 8, left, maxHeight: Math.max(spaceAbove, 200) });
      } else {
        setDropdownPos({ top: rect.bottom + 8, left, maxHeight: spaceBelow });
      }
    }
    setShowDatePicker(prev => !prev);
  };
  
  const closeDropdown = () => {
    setShowDatePicker(false);
  };
  
  // Count applications added today (robust against different date formats)
  const addedTodayCount = React.useMemo(() => {
    if (usesRemoteSummary) return feedSummary?.counts.today ?? null;
    if (!applications || !Array.isArray(applications)) return 0;
    const referenceDate = new Date();
    return applications.filter(app => isPlanningFeedAddedToday(app, referenceDate)).length;
  }, [applications, feedSummary, usesRemoteSummary]);

  const addedYesterdayCount = React.useMemo(() => {
    if (usesRemoteSummary) return feedSummary?.counts.yesterday ?? null;
    if (!applications || !Array.isArray(applications)) return 0;
    const referenceDate = new Date();
    return applications.filter(app => isPlanningFeedAddedYesterday(app, referenceDate)).length;
  }, [applications, feedSummary, usesRemoteSummary]);

  const clearDateFilter = () => {
    trackPlanningUpdatesEvent('planning_updates_date_filter_cleared', {
      placement: analyticsPlacement,
      status: 'cleared',
      metadata: {
        had_custom_date: Boolean(filterByDateAdded),
        today_filter_active: Boolean(filterByAddedToday),
        yesterday_filter_active: Boolean(filterByAddedYesterday)
      }
    });
    setSelectedDate('');
    setFilterByDateAdded(null);
    setShowDatePicker(false);
    const today = new Date();
    setVisibleMonth(today.getMonth());
    setVisibleYear(today.getFullYear());
  };

  const formatSelectedDateLabel = () => {
    if (!selectedDate) return 'Pick a date';

    try {
      const parsed = new Date(selectedDate);
      if (Number.isNaN(parsed.getTime())) {
        return 'Filter by Date';
      }

      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short'
      }).format(parsed);
    } catch {
      return 'Filter by Date';
    }
  };

  const dateIsActive = Boolean(filterByDateAdded);

  const formatDate = (date) => {
    if (!(date instanceof Date)) return null;
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = useMemo(() => new Date(), []);

  const calendarDays = useMemo(() => {
    const firstOfMonth = new Date(visibleYear, visibleMonth, 1);
    const monthStartWeekday = (firstOfMonth.getDay() + 6) % 7; // convert Sunday start to Monday start
    const gridStartDate = new Date(visibleYear, visibleMonth, 1 - monthStartWeekday);

    const days = [];
    for (let i = 0; i < 42; i += 1) {
      const current = new Date(gridStartDate);
      current.setDate(gridStartDate.getDate() + i);
      const iso = formatDate(current);
      days.push({
        key: `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`,
        date: current,
        iso,
        isCurrentMonth: current.getMonth() === visibleMonth,
        isToday:
          current.getFullYear() === today.getFullYear() &&
          current.getMonth() === today.getMonth() &&
          current.getDate() === today.getDate(),
        isSelected: selectedDate === iso
      });
    }
    return days;
  }, [selectedDate, today, visibleMonth, visibleYear]);

  const handleSelectDay = (date) => {
    const formatted = formatDate(date);
    if (!formatted) return;
    trackPlanningUpdatesEvent('planning_updates_custom_date_selected', {
      placement: analyticsPlacement,
      status: 'custom_date',
      metadata: {
        selected_month: String(date.getMonth() + 1),
        selected_year: String(date.getFullYear()),
        is_current_month: date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
      }
    });
    setSelectedDate(formatted);
    setFilterByDateAdded(formatted);
    setShowDatePicker(false);
  };

  const handleMonthChange = (delta) => {
    const next = new Date(visibleYear, visibleMonth + delta, 1);
    trackPlanningUpdatesEvent('planning_updates_date_picker_month_changed', {
      placement: analyticsPlacement,
      status: delta > 0 ? 'next' : 'previous',
      metadata: {
        visible_month: String(next.getMonth() + 1),
        visible_year: String(next.getFullYear())
      }
    });
    setVisibleMonth(next.getMonth());
    setVisibleYear(next.getFullYear());
  };

  const weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const monthLabel = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });
    return formatter.format(new Date(visibleYear, visibleMonth, 1));
  }, [visibleMonth, visibleYear]);
  
  return (
    <div className="pr-field pr-date-filter w-full">
      <span className="pr-field-label">Date added</span>

      <div className="pr-date-filter-controls">
        <button
          type="button"
          onClick={handleAddedTodayClick}
          aria-pressed={!!filterByAddedToday}
          className={`pr-date-filter-button ${filterByAddedToday ? 'pr-date-filter-button--active' : ''}`}
          title="Show applications added today"
        >
          Today
          <span className="pr-date-filter-count" aria-label={addedTodayCount === null ? 'Count unavailable' : undefined}>{addedTodayCount ?? '—'}</span>
        </button>
        <button
          type="button"
          onClick={handleAddedYesterdayClick}
          aria-pressed={!!filterByAddedYesterday}
          className={`pr-date-filter-button ${filterByAddedYesterday ? 'pr-date-filter-button--active' : ''}`}
          title="Show applications added yesterday"
        >
          Yesterday
          <span className="pr-date-filter-count" aria-label={addedYesterdayCount === null ? 'Count unavailable' : undefined}>{addedYesterdayCount ?? '—'}</span>
        </button>

        <button
          ref={buttonRef}
          type="button"
          onClick={handleClickDateButton}
          className={`pr-field-trigger pr-date-filter-picker ${dateIsActive ? 'pr-field-trigger--active' : ''}`}
          aria-expanded={showDatePicker}
        >
          <span className="inline-flex min-w-0 items-center gap-2">
            <CalendarIcon className="h-4 w-4 shrink-0 text-[var(--c-text-secondary)]" />
            <span className="truncate">{formatSelectedDateLabel()}</span>
          </span>
          <svg className={`h-4 w-4 shrink-0 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

        {/* Portal renders outside any transform/overflow ancestor */}
        {portalMounted && createPortal(
          <AnimatePresence>
            {showDatePicker && dropdownPos && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.18 }}
              style={{
                position: 'fixed',
                left: dropdownPos.left,
                maxHeight: dropdownPos.maxHeight,
                overflowY: 'auto',
                zIndex: 9999,
                ...(dropdownPos.top !== undefined
                  ? { top: dropdownPos.top }
                  : { bottom: dropdownPos.bottom })
              }}
              className="planning-register w-80 max-w-[calc(100vw-16px)] border border-[var(--c-border)] border-t-2 border-t-[var(--c-text-primary)] bg-[var(--c-surface)]"
            >
              <div className="space-y-5 px-5 py-5">
                {/* Filter by Specific Date */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium uppercase tracking-[0.08em] text-[var(--c-text-tertiary)]">Select Date</p>
                    <button
                      type="button"
                      onClick={() => {
                        const now = new Date();
                        setVisibleMonth(now.getMonth());
                        setVisibleYear(now.getFullYear());
                      }}
                      className="text-xs font-medium text-[var(--c-text-secondary)] transition hover:text-[var(--c-text-primary)]"
                    >
                      Today
                    </button>
                  </div>

                  <div className="border border-[var(--c-border)] bg-[var(--c-bg)]">
                    <div className="flex items-center justify-between border-b border-[var(--c-border)] px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleMonthChange(-1)}
                        className="inline-flex h-8 w-8 items-center justify-center text-[var(--c-text-secondary)] transition hover:text-[var(--c-text-primary)]"
                        aria-label="Previous month"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <p className="font-['Newsreader',Georgia,serif] text-sm font-medium text-[var(--c-text-primary)]">{monthLabel}</p>
                      <button
                        type="button"
                        onClick={() => handleMonthChange(1)}
                        className="inline-flex h-8 w-8 items-center justify-center text-[var(--c-text-secondary)] transition hover:text-[var(--c-text-primary)]"
                        aria-label="Next month"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 px-4 pt-3 pr-data text-[10px] uppercase">
                      {weekdayLabels.map((label) => (
                        <span key={label} className="text-center">
                          {label}
                        </span>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1 px-3 pb-3 pt-2">
                      {calendarDays.map((day) => {
                        const stateClasses = [
                          day.isCurrentMonth ? 'text-[var(--c-text-primary)]' : 'text-[var(--c-text-tertiary)] opacity-60',
                          day.isSelected
                            ? 'bg-[var(--c-text-primary)] text-[var(--c-bg)]'
                            : 'hover:bg-[var(--c-surface-hover)]',
                          day.isToday && !day.isSelected ? 'ring-1 ring-[var(--c-accent)]' : '',
                        ].join(' ');

                        return (
                          <button
                            key={day.key}
                            type="button"
                            onClick={() => handleSelectDay(day.date)}
                            className={`relative flex h-9 w-full items-center justify-center text-sm font-medium transition ${stateClasses}`}
                          >
                            {day.date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={clearDateFilter}
                    className="pr-textlink flex-1 justify-center"
                  >
                    Clear
                  </button>
                  <button
                    onClick={closeDropdown}
                    className="pr-button flex-1"
                  >
                    Done
                  </button>
                </div>
              </div>
            </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default ScrapedDateFilter; 
